import { useLinkAccessEvents, type ShareLinkEvent } from "../../lib/api/useSharedLinks";
import { Spinner } from "../ui/Spinner";
import { cn } from "../../lib/cn";

type Tone = "ok" | "denied" | "warn" | "neutral";

const TONE_CLASSES: Record<Tone, string> = {
  ok: "text-emerald-700",
  denied: "text-red-700",
  warn: "text-amber-700",
  neutral: "text-neutral-600",
};

// Map the raw audit action to a human label. `resolve` = a caregiver opened the
// profile; every `denied_*` is a rejected attempt; `rate_limited` was throttled.
function describeAction(action: string): { label: string; tone: Tone } {
  switch (action) {
    case "resolve":
      return { label: "Opened", tone: "ok" };
    case "denied_inactive":
    case "denied_expired":
    case "denied_invalid_token":
      return { label: "Denied", tone: "denied" };
    case "rate_limited":
      return { label: "Rate limited", tone: "warn" };
    default:
      return { label: "Activity", tone: "neutral" };
  }
}

function statusClasses(status: number): string {
  if (status < 300) return "bg-emerald-50 text-emerald-700";
  if (status < 500) return "bg-amber-50 text-amber-700";
  return "bg-red-50 text-red-700";
}

function formatRelative(iso: string): string {
  const date = new Date(iso);
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "Just now";
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 30) return `${diffDay}d ago`;
  return date.toLocaleDateString();
}

function EventRow({ event }: { event: ShareLinkEvent }) {
  const { label, tone } = describeAction(event.action);
  return (
    <tr>
      <td className="px-3 py-2 align-middle text-neutral-600">
        <time dateTime={event.accessedAt} title={new Date(event.accessedAt).toLocaleString()}>
          {formatRelative(event.accessedAt)}
        </time>
      </td>
      <td className={cn("px-3 py-2 align-middle font-medium", TONE_CLASSES[tone])}>{label}</td>
      <td className="px-3 py-2 text-right align-middle">
        <span
          className={cn(
            "inline-block rounded-full px-2 py-0.5 font-mono text-[10px] font-semibold",
            statusClasses(event.httpStatus),
          )}
        >
          {event.httpStatus}
        </span>
      </td>
    </tr>
  );
}

/**
 * Compact, owner-only audit log for a single share link. Renders one row per
 * access attempt (relative time · human event label · HTTP status). Loading,
 * empty, and error states are all handled; the error branch switches on the
 * stable `error.code`. No raw token is ever shown here.
 */
export function LinkAccessEventsPanel({ linkId }: { linkId: string }) {
  const { data: events, isLoading, error } = useLinkAccessEvents(linkId);

  if (isLoading) {
    return (
      <div
        role="status"
        className="flex items-center gap-2 px-1 py-2 text-xs text-neutral-600"
      >
        <Spinner size="sm" />
        Loading access log…
      </div>
    );
  }

  if (error) {
    const message =
      error.code === "RATE_LIMITED"
        ? "Too many requests — try again in a moment."
        : error.code === "NOT_FOUND"
          ? "This link no longer exists."
          : error.code === "UNAUTHORIZED" || error.code === "FORBIDDEN"
            ? "You don't have permission to view this log."
            : "Couldn't load the access log. Please try again.";
    return (
      <p className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">{message}</p>
    );
  }

  if (!events || events.length === 0) {
    return (
      <p className="rounded-xl bg-white/70 px-3 py-2 text-xs text-neutral-600 ring-1 ring-sky-100">
        No access recorded yet.
      </p>
    );
  }

  return (
    <div className="max-h-64 overflow-y-auto rounded-xl bg-white/70 ring-1 ring-sky-100">
      <table className="w-full text-left text-xs">
        <caption className="sr-only">Access log for this share link</caption>
        <thead className="bg-sky-50/80 text-[10px] uppercase tracking-wide text-neutral-600">
          <tr>
            <th scope="col" className="px-3 py-2 font-semibold">
              When
            </th>
            <th scope="col" className="px-3 py-2 font-semibold">
              Event
            </th>
            <th scope="col" className="px-3 py-2 text-right font-semibold">
              Status
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-sky-100/70">
          {events.map((event) => (
            <EventRow key={event.id} event={event} />
          ))}
        </tbody>
      </table>
    </div>
  );
}
